import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import travelTypeObject from '@/entities/travelType/interface'
import employeeObject from '@/entities/employee/interface'
import redirectObject from '@/entities/redirect/interface'
import partnerTenantObject from '@/entities/partnerTenant/interface'

import {
  ALL_TENANTS_QUERY,
  SINGLE_TENANT_QUERY,
  TENANTS_DEPENDENTS_QUERY,
  CREATE_TENANT_MUTATION,
  UPDATE_TENANT_MUTATION,
  DELETE_TENANT_MUTATION,
  FORCE_DELETE_TENANT_MUTATION,
  ADD_TENANT_TRAVEL_TYPES_MUTATION,
  REMOVE_TENANT_TRAVEL_TYPES_MUTATION,
  CREATE_TENANT_TRAVEL_TYPES_MUTATION,
  DELETE_TENANT_TRAVEL_TYPES_MUTATION,
  UPDATE_TENANT_TRAVEL_TYPES_ORDER_MUTATION,
  ADD_TENANT_EMPLOYEES_MUTATION,
  REMOVE_TENANT_EMPLOYEES_MUTATION,
  CREATE_TENANT_EMPLOYEES_MUTATION,
  DELETE_TENANT_EMPLOYEES_MUTATION,
  UPDATE_TENANT_EMPLOYEES_ORDER_MUTATION,
  ADD_TENANT_REDIRECTS_MUTATION,
  REMOVE_TENANT_REDIRECTS_MUTATION,
  CREATE_TENANT_REDIRECTS_MUTATION,
  DELETE_TENANT_REDIRECTS_MUTATION,
  UPDATE_TENANT_REDIRECTS_ORDER_MUTATION,
  ADD_TENANT_PARTNER_TENANTS_MUTATION,
  REMOVE_TENANT_PARTNER_TENANTS_MUTATION,
  CREATE_TENANT_PARTNER_TENANTS_MUTATION,
  DELETE_TENANT_PARTNER_TENANTS_MUTATION,
  UPDATE_TENANT_PARTNER_TENANTS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const tenantRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_TENANT_QUERY, variables).then((data: any) => {
      return hydrateObject(data.tenant)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_TENANTS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.tenants)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_TENANT_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.tenant),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_TENANTS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.tenants),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(TENANTS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([TENANTS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_TENANT_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.tenant)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_TENANT_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.tenant)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_TENANT_MUTATION, variables).then((data: any) => {
      return data.deleteTenant
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_TENANT_MUTATION, variables).then((data: any) => {
      return data.forceDeleteTenant
    })
  },

  assignTravelTypes: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypeIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TENANT_TRAVEL_TYPES_MUTATION, variables)
  },

  unassignTravelTypes: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypeIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TENANT_TRAVEL_TYPES_MUTATION, variables)
  },

  createTravelTypes: async (id: string, items: Array<travelTypeObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypes: items,
      },
    }
    return graphQLClient.request(CREATE_TENANT_TRAVEL_TYPES_MUTATION, variables)
  },

  deleteTravelTypes: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypeIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TENANT_TRAVEL_TYPES_MUTATION, variables)
  },

  updateTravelTypesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelTypeIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TENANT_TRAVEL_TYPES_ORDER_MUTATION, variables)
  },

  assignEmployees: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        employeeIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TENANT_EMPLOYEES_MUTATION, variables)
  },

  unassignEmployees: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        employeeIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TENANT_EMPLOYEES_MUTATION, variables)
  },

  createEmployees: async (id: string, items: Array<employeeObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        employees: items,
      },
    }
    return graphQLClient.request(CREATE_TENANT_EMPLOYEES_MUTATION, variables)
  },

  deleteEmployees: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        employeeIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TENANT_EMPLOYEES_MUTATION, variables)
  },

  updateEmployeesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        employeeIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TENANT_EMPLOYEES_ORDER_MUTATION, variables)
  },

  assignRedirects: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        redirectIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TENANT_REDIRECTS_MUTATION, variables)
  },

  unassignRedirects: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        redirectIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TENANT_REDIRECTS_MUTATION, variables)
  },

  createRedirects: async (id: string, items: Array<redirectObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        redirects: items,
      },
    }
    return graphQLClient.request(CREATE_TENANT_REDIRECTS_MUTATION, variables)
  },

  deleteRedirects: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        redirectIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TENANT_REDIRECTS_MUTATION, variables)
  },

  updateRedirectsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        redirectIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TENANT_REDIRECTS_ORDER_MUTATION, variables)
  },

  assignPartnerTenants: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partnerTenantIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TENANT_PARTNER_TENANTS_MUTATION, variables)
  },

  unassignPartnerTenants: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partnerTenantIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TENANT_PARTNER_TENANTS_MUTATION, variables)
  },

  createPartnerTenants: async (id: string, items: Array<partnerTenantObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partnerTenants: items,
      },
    }
    return graphQLClient.request(CREATE_TENANT_PARTNER_TENANTS_MUTATION, variables)
  },

  deletePartnerTenants: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partnerTenantIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TENANT_PARTNER_TENANTS_MUTATION, variables)
  },

  updatePartnerTenantsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        partnerTenantIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TENANT_PARTNER_TENANTS_ORDER_MUTATION, variables)
  },
}

export default tenantRepository
